/**
 * API Key Permissions
 *
 * These permissions should match what's configured in the WorkOS Dashboard
 * under Organization Settings > Roles & Permissions.
 *
 * Permissions control access to different features and resources via API keys.
 */

/**
 * Available API key permission values
 * NOTE: These must match the exact slugs configured in WorkOS Dashboard
 */
export const API_KEY_PERMISSIONS = {
  ACCOUNT_CREATE: 'account:create',
  ACCOUNT_DELETE: 'account:delete',
  ACCOUNT_READ: 'account:read',
  ACCOUNT_UPDATE: 'account:update',

  API_KEYS_MANAGE: 'api-keys:manage',

  CHATS_CREATE: 'chats:create',
  CHATS_DELETE: 'chats:delete',
  CHATS_READ: 'chats:read',
  CHATS_UPDATE: 'chats:update',

  DOCUMENTS_CREATE: 'documents:create',
  DOCUMENTS_DELETE: 'documents:delete',
  DOCUMENTS_READ: 'documents:read',
  DOCUMENTS_UPDATE: 'documents:update',

  PLAYBOOKS_CREATE: 'playbooks:create',
  PLAYBOOKS_DELETE: 'playbooks:delete',
  PLAYBOOKS_READ: 'playbooks:read',
  PLAYBOOKS_UPDATE: 'playbooks:update',

  REMINDERS_CREATE: 'reminders:create',
  REMINDERS_DELETE: 'reminders:delete',
  REMINDERS_READ: 'reminders:read',
  REMINDERS_UPDATE: 'reminders:update',

  REPORTS_CREATE: 'reports:create',
  REPORTS_DELETE: 'reports:delete',
  REPORTS_READ: 'reports:read',
  REPORTS_UPDATE: 'reports:update',

  TEMPLATES_CREATE: 'templates:create',
  TEMPLATES_DELETE: 'templates:delete',
  TEMPLATES_READ: 'templates:read',
  TEMPLATES_UPDATE: 'templates:update',

  WORKFLOWS_CREATE: 'workflows:create',
  WORKFLOWS_DELETE: 'workflows:delete',
  WORKFLOWS_READ: 'workflows:read',
  WORKFLOWS_UPDATE: 'workflows:update',
} as const;

/**
 * All valid permission slugs as a Set for runtime validation.
 */
export const VALID_API_KEY_PERMISSIONS = new Set<string>(Object.values(API_KEY_PERMISSIONS));

/**
 * Default permissions granted to all new API keys when none are specified.
 * Defaults are read-only for the API-accessible resource groups.
 */
export const DEFAULT_API_KEY_PERMISSIONS = [
  API_KEY_PERMISSIONS.ACCOUNT_READ,
  API_KEY_PERMISSIONS.CHATS_READ,
  API_KEY_PERMISSIONS.DOCUMENTS_READ,
  API_KEY_PERMISSIONS.PLAYBOOKS_READ,
  API_KEY_PERMISSIONS.REPORTS_READ,
  API_KEY_PERMISSIONS.REMINDERS_READ,
  API_KEY_PERMISSIONS.TEMPLATES_READ,
  API_KEY_PERMISSIONS.WORKFLOWS_READ,
] as const;

/**
 * Type representing a valid API key permission
 */
export type ApiKeyPermission = (typeof API_KEY_PERMISSIONS)[keyof typeof API_KEY_PERMISSIONS];
